/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Col, Form, Radio, Row } from "antd";
import { InputField, InputPhone } from "../../../../../../atoms/FormFields";
import { AddressComponent, Place } from "../../../../../Company/ducks/types";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import SuccessMessage from "../../../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../../../atoms/Toasts/ErrorMessage";
import { useAppDispatch } from "../../../../../../config/hooks";
import { getCompanyFieldsById } from "../../../../../CompanySettings/TagsCategories/ducks/actions";
import {
  AmountIcon,
  PenIcon,
  StarFilledIcon,
} from "../../../../../../atoms/CustomIcons";
import { AutoCompleteField } from "../../../../../../atoms/FormFields/AutoComplete";
import { getPropertyFormData } from "../../../../ducks/actions";
import { createClient } from "../../../../../Clients/ducks/services";
import { SelectedClientType } from "../../../../ducks/types";

interface FormValues {
  first_name: string;
  last_name: string;
  title: string;
  address: string;
  email: string;
  phone: string;
  category_ids: string;
  company_name: string;
  companyAddress: string;
  companyCategoryIds: string;
  preferred_method_of_contact: string;
}

interface PropsType {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: string;
  onClose: () => void;
  newClientPopUp?: boolean;
  data: SelectedClientType | null;
}

export default function ClientIndividual(props: PropsType) {
  const { setLoading, companyId, onClose, data, newClientPopUp } = props;
  const [method, setMethod] = useState("any");
  const dispatch = useAppDispatch();
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "CLIENTS",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    const payload = {
      name: val?.first_name?.trim(),
      last_name: val?.last_name?.trim(),
      address: val?.address,
      city: city,
      state: state,
      type: "individual",
      category_ids: val?.category_ids,
      phone: val?.phone,
      email: val?.email,
      title: val?.title,
      company_id: companyId,
      preferred_method_of_contact: val?.preferred_method_of_contact,
    };

    createClient(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("New client added.");
        dispatch(getPropertyFormData(companyId));
        onClose();
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (data && !newClientPopUp) {
      setValue("first_name", data?.label);
      // setValue("title", data?.title);
      setValue("address", data?.address);
      setValue("phone", data?.phone);
      setValue("email", data?.email);
      // setValue("category_ids", data?.category_ids);
    } else {
      reset();
    }
  }, [data, setValue, newClientPopUp, reset]);

  return (
    <Form
      className="propertyForm"
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.preventDefault();
      }}
    >
      <Row gutter={8}>
        <Col span={12}>
          <InputField
            fieldName="first_name"
            label="First name"
            control={control}
            defaultValue=""
            iProps={{
              placeholder: "First name",
            }}
            isRequired
            rules={{
              required: "First name is required.",
            }}
            validate={errors.first_name && "error"}
            validMessage={errors.first_name && errors.first_name.message}
          />
        </Col>
        <Col span={12}>
          <InputField
            fieldName="last_name"
            label="Last name"
            control={control}
            defaultValue=""
            iProps={{
              placeholder: "Last name",
            }}
            isRequired
            rules={{
              required: "Last name is required.",
            }}
            validate={errors.last_name && "error"}
            validMessage={errors.last_name && errors.last_name.message}
          />
        </Col>
        <Col
          span={24}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ width: "calc(100% - 70px)" }}>
            <InputField
              fieldName="title"
              label="Title"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Enter title",
              }}
              isRequired
              rules={{
                required: "title is required.",
              }}
              validate={errors.title && "error"}
              validMessage={errors.title && errors.title.message}
            />
          </div>
          <div
            className="actionBtns"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "7px",
              width: "70px",
              marginTop: "5px",
            }}
          >
            <Button type="text">
              <StarFilledIcon />
            </Button>
            <Button type="text">
              <PenIcon />
            </Button>
            <Button type="text">
              <AmountIcon />
            </Button>
          </div>
        </Col>
        <Col span={24}>
          <AutoCompleteField
            fieldName="address"
            label="Billing address"
            control={control}
            defaultValue=""
            iProps={{
              placeholder: "Search address",
            }}
            onPlaceChange={(place: Place) => setGoogleAddress(place)}
            tooltipTitle="This is where invoices or payment statements are sent."
          />
        </Col>
        <Col span={12}>
          <InputPhone fieldName="phone" label="Phone" control={control} />
        </Col>
        <Col span={12}>
          <InputField
            fieldName="email"
            label="Email"
            control={control}
            defaultValue=""
            iProps={{
              placeholder: "Enter email",
              autoComplete: "email",
              maxLength: 100,
            }}
            isRequired
            rules={{
              required: "Email address is required.",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "Please enter a valid email address.",
              },
            }}
            validate={errors.email && "error"}
            validMessage={errors.email && errors.email.message}
          />
        </Col>
        <Col span={24} style={{ display: "flex", marginBottom: "16px" }}>
          <div style={{ fontWeight: "500" }}>Preferred method of contact:</div>
          <div className="preferredMethodRadio">
            <Radio.Group
              onChange={(e) => setMethod(e.target.value)}
              value={method}
            >
              <Radio value="any">Any</Radio>
              <Radio value="phone">Phone</Radio>
              <Radio value="email">Email</Radio>
            </Radio.Group>
          </div>
        </Col>
        <Col span={24}>
          <div
            className="centered"
            style={{
              paddingTop: "4px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              htmlType="button"
              className="outlined-btn"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button htmlType="submit" type="primary" className="mint-btn">
              Save client
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
}
